import React, { useState } from 'react';
import Slider, { Settings } from 'react-slick';
import { IconButton, Typography } from '@guildeducationinc/recess';
import ChevronLeftIcon from '@guildeducationinc/recess/icons/ChevronLeftIcon';
import ChevronRightIcon from '@guildeducationinc/recess/icons/ChevronRightIcon';
import { MilestoneKey } from '@src/context/MilestoneContext';
import { MAX_TABLET_WIDTH, useIsMobile, useIsTablet } from '@src/utilities/screenResizeUtility';
import SkeletonForLLC from '@src/components/Shared/SkeletonForLLC/SkeletonForLLC';
import { useLearningLifecyclesContext } from '@src/context/LearningLifecyclesContext';
import Milestone from '../Milestone/Milestone';
import GreetingHeader from '../GreetingHeader/GreetingHeader';
import ImageCard from '../ImageCard/ImageCard';
import styles from './Shelf.module.scss';
import LifecycleSelect from '../LifecycleSelect/LifecycleSelect';

export interface ShelfProps {
  milestones: MilestoneKey[];
  numberOfMilestonesToShow: number;
}

interface ArrowProps {
  currentSlide?: number;
  isSingleSlide?: boolean;
  milestones: MilestoneKey[];
  slickRef: any;
}

/**
 * @param milestones - All milestones to be displayed.
 * @param numberOfMilestonesToShow - The number of milestones that are visible without scrolling.
 * @returns
 */

const PrevArrow: React.FC<ArrowProps> = ({ currentSlide, isSingleSlide, milestones, slickRef }) => {
  let taskCounterString = null;
  if (isSingleSlide) {
    taskCounterString = `Task ${currentSlide ? currentSlide + 1 : 1} / ${milestones.length}`;
  }
  if (!isSingleSlide && currentSlide !== undefined) {
    if (milestones.length >= 3 && currentSlide < 3) {
      taskCounterString = `Tasks 3 / ${milestones.length}`;
    } else {
      taskCounterString = `Tasks ${milestones.length} / ${milestones.length}`;
    }
  }

  return (
    <div className={styles.navigationContainer}>
      <Typography variant='title4' className={styles.taskCount}>
        {taskCounterString}
      </Typography>
      <IconButton
        appearance='outline'
        aria-label='Previous page of shelf'
        className={styles.prevArrow}
        disabled={currentSlide === 0}
        icon={<ChevronLeftIcon size={24} />}
        label='next page'
        onClick={() => slickRef.current.slickPrev()}
      />
    </div>
  );
};

const NextArrow: React.FC<ArrowProps> = ({ currentSlide, milestones, slickRef }) => (
  <IconButton
    appearance='outline'
    aria-label='Next page of shelf'
    className={styles.nextArrow}
    disabled={currentSlide === milestones.length - 1}
    icon={<ChevronRightIcon size={24} />}
    label='next page'
    onClick={() => slickRef.current.slickNext()}
  />
);

const Shelf: React.FC<ShelfProps> = ({ milestones, numberOfMilestonesToShow }) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { learningLifecycles } = useLearningLifecyclesContext();
  const slickRef = React.useRef<any>();
  const [startingSlide, setStartingSlide] = useState<number>(0);

  // These are the settings to configure the react slick-shelf component

  const settings: Settings = {
    infinite: false,
    initialSlide: startingSlide,
    speed: 500,
    slidesToShow: numberOfMilestonesToShow,
    slidesToScroll: numberOfMilestonesToShow,
    accessibility: true,
    prevArrow: (
      <PrevArrow isSingleSlide={isMobile || isTablet} milestones={milestones} slickRef={slickRef} />
    ),
    nextArrow: <NextArrow milestones={milestones} slickRef={slickRef} />,
    responsive: [
      {
        breakpoint: MAX_TABLET_WIDTH,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          swipeToSlide: true,
        },
      },
    ],
  };

  // Empty milestones allow to always scroll through 3 (per design) milestones at a time on desktop
  const numberOfEmptyMilestones =
    isMobile || isTablet
      ? 0
      : settings.slidesToShow! - (milestones.length % settings.slidesToShow!);
  const emptyMilestones = [...Array.from(Array(numberOfEmptyMilestones).keys())];

  const Carousel = () => {
    return (
      <Slider ref={slickRef} {...settings} data-testid='shelf-slider' className={styles.sliderMain}>
        {milestones.map((milestone, index) => {
          return (
            <Milestone
              order={index + 1}
              key={milestone}
              milestoneKey={milestone}
              setStartingSlide={setStartingSlide}
            />
          );
        })}

        {emptyMilestones.map((element) => {
          return <div key={element} data-testid={`empty-placeholder-${element}`}></div>;
        })}
      </Slider>
    );
  };

  const showMobileCarousel = (isMobile || isTablet) && milestones.length > 1;
  const showDesktopCarousel = (!isMobile || !isTablet) && milestones.length > 3;
  const showImage = !isMobile && !isTablet && milestones.length <= 2;
  const showLifecycleSelect = !!learningLifecycles?.length && learningLifecycles?.length > 1;

  return (
    <div className={styles.shelfBase}>
      <div className={styles.shelfContainer}>
        <GreetingHeader />
        <SkeletonForLLC label='learning-lifecycles' height='275px' marginBottom='36px'>
          <div className={styles.controlWrapper}>{showLifecycleSelect && <LifecycleSelect />}</div>
          {showMobileCarousel || showDesktopCarousel ? (
            <Carousel />
          ) : (
            <div className={styles.milestonesFlex} data-testid='milestone-shelf-module'>
              {milestones.map((milestone, index) => {
                return (
                  <Milestone
                    order={index + 1}
                    key={milestone}
                    milestoneKey={milestone}
                    setStartingSlide={setStartingSlide}
                  />
                );
              })}
              {showImage && milestones[0] ? <ImageCard milestoneName={milestones[0]} /> : null}
            </div>
          )}
        </SkeletonForLLC>
      </div>
    </div>
  );
};

export default Shelf;
